import React, { useContext, useState, useEffect } from "react";
import { Range } from "../utils/range";

const STORAGE_KEY = "kpiRanges";

/**
 * Représente un KPI avec son range (période)
 * Ex: { kpiKey: "kpi_pft", range: Range.Direct }
 */
export type KpiRange = {
  kpiKey: string;
  range: Range;
};

/**
 * Type du contexte : stock d'un tableau de KPIs avec leurs ranges
 */
type RangeContextType = {
  kpiRanges: KpiRange[];
  
  setKpiRange: (kpiKey: string, range: Range) => void;
  
  setKpiRanges: (kpiRanges: KpiRange[]) => void;
  
  getKpiRange: (kpiKey: string) => Range | undefined;
  
  clearKpiRanges: () => void;
};

const RangeContext = React.createContext<RangeContextType>({
  kpiRanges: [],
  setKpiRange: () => {},
  setKpiRanges: () => {},
  getKpiRange: () => undefined,
  clearKpiRanges: () => {},
});

type RangeProviderProps = {
  children: React.ReactNode;
};


export const RangeProvider: React.FunctionComponent<RangeProviderProps> = ({
  children,
}: RangeProviderProps) => {
  const [kpiRanges, setKpiRangesState] = useState<KpiRange[]>(() => {
    // Initialiser depuis localStorage
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  // Persister dans localStorage à chaque changement
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(kpiRanges));
    } catch {
      console.error("Failed to save kpiRanges to localStorage");
    }
  }, [kpiRanges]);

  const setKpiRange = (kpiKey: string, range: Range) => {
    setKpiRangesState((prev) => {
      const existing = prev.findIndex((item) => item.kpiKey === kpiKey);
      if (existing >= 0) {
        // Update existing
        const updated = [...prev];
        updated[existing] = { kpiKey, range };
        return updated;
      }
      // Add new
      return [...prev, { kpiKey, range }];
    });
  };

  const setKpiRanges = (newRanges: KpiRange[]) => {
    setKpiRangesState(newRanges);
  };

  const getKpiRange = (kpiKey: string): Range | undefined => {
    const item = kpiRanges.find((item) => item.kpiKey === kpiKey);
    return item?.range;
  };

  const clearKpiRanges = () => {
    setKpiRangesState([]);
  };

  return (
    <RangeContext.Provider
      value={{
        kpiRanges,
        setKpiRange,
        setKpiRanges,
        getKpiRange,
        clearKpiRanges,
      }}
    >
      {children}
    </RangeContext.Provider>
  );
};

export const useRange: () => RangeContextType = () => {
  const context = useContext(RangeContext);
  if (!context) {
    throw new Error("useRange must be used within a RangeProvider");
  }
  return context;
};
